import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import BlockRow from './BlockRow';
const WRAPPER_TYPES = new Set(['section', 'container', 'row', 'column']);
export default function BlockTree({ blocks, selectedId, onSelect, onReorder, onDuplicate, onDelete, onAddChild }) {
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
    const containerMap = useMemo(() => buildContainerMap(blocks), [blocks]);
    function handleDragEnd(e) {
        const { active, over } = e;
        if (!over || active.id === over.id)
            return;
        const fromContainer = findContainerForId(containerMap, String(active.id));
        if (!fromContainer)
            return;
        const toContainer = findContainerForId(containerMap, String(over.id));
        if (!toContainer)
            return;
        if (fromContainer === toContainer) {
            // Same-container reorder.
            const ids = containerMap[fromContainer];
            const oldIndex = ids.indexOf(String(active.id));
            const newIndex = ids.indexOf(String(over.id));
            if (oldIndex < 0 || newIndex < 0)
                return;
            const next = reorderInTree(blocks, fromContainer, arrayMove(ids, oldIndex, newIndex));
            onReorder(next);
            return;
        }
        // Phase L push 2: cross-container move. Remove from source, insert into
        // target before / after the over.id (heuristic: insert AFTER the target).
        const next = moveBlockAcrossContainers(blocks, String(active.id), toContainer, String(over.id));
        if (next)
            onReorder(next);
    }
    if (!blocks.length)
        return _jsx("div", { className: "muted pad", children: "No blocks yet \u2014 add one above." });
    return (_jsx(DndContext, { sensors: sensors, collisionDetection: closestCenter, onDragEnd: handleDragEnd, children: _jsx(BlockLevel, { blocks: blocks, depth: 0, containerId: "__root__", selectedId: selectedId, onSelect: onSelect, onDuplicate: onDuplicate, onDelete: onDelete, onAddChild: onAddChild }) }));
}
function BlockLevel({ blocks, depth, containerId, selectedId, onSelect, onDuplicate, onDelete, onAddChild, }) {
    const ids = blocks.map((b) => b.id);
    return (_jsx(SortableContext, { id: containerId, items: ids, strategy: verticalListSortingStrategy, children: _jsx("div", { className: "block-level", children: blocks.map((b) => (_jsxs("div", { children: [_jsx(BlockRow, { block: b, depth: depth, selected: selectedId === b.id, onSelect: () => onSelect(b.id), onDuplicate: () => onDuplicate(b.id), onDelete: () => onDelete(b.id), onAddChild: WRAPPER_TYPES.has(b.type) && onAddChild ? () => onAddChild(b.id) : undefined }), b.children.length > 0 && (_jsx(BlockLevel, { blocks: b.children, depth: depth + 1, containerId: b.id, selectedId: selectedId, onSelect: onSelect, onDuplicate: onDuplicate, onDelete: onDelete, onAddChild: onAddChild }))] }, b.id))) }) }));
}
/** Map of containerId → ordered child ids. '__root__' is the top level; other keys are wrapper-block ids. */
function buildContainerMap(blocks) {
    const map = { __root__: blocks.map((b) => b.id) };
    const walk = (bs) => {
        for (const b of bs) {
            if (b.children.length) {
                map[b.id] = b.children.map((c) => c.id);
                walk(b.children);
            }
        }
    };
    walk(blocks);
    return map;
}
function findContainerForId(map, id) {
    for (const [containerId, ids] of Object.entries(map)) {
        if (ids.includes(id))
            return containerId;
    }
    return null;
}
/** Apply a new ordering to a single container; rebuild the full tree. */
function reorderInTree(blocks, containerId, newOrder) {
    if (containerId === '__root__') {
        return reorderArrayBy(blocks, newOrder);
    }
    return blocks.map((b) => {
        if (b.id === containerId) {
            return { ...b, children: reorderArrayBy(b.children, newOrder) };
        }
        if (b.children.length) {
            return { ...b, children: reorderInTree(b.children, containerId, newOrder) };
        }
        return b;
    });
}
function reorderArrayBy(arr, order) {
    const byId = new Map(arr.map((b) => [b.id, b]));
    return order.map((id) => byId.get(id)).filter(Boolean);
}
// ---------- Phase L push 2: cross-container move ----------
/**
 * Remove the block with `srcId` from anywhere in the tree, then re-insert it
 * into the parent identified by `targetParentId` immediately AFTER the block
 * with `overId`. Returns the new tree, or null if any step failed.
 */
function moveBlockAcrossContainers(blocks, srcId, targetParentId, overId) {
    // 1. Detach the dragged block from wherever it lives.
    const detached = removeFromTree(blocks, srcId);
    if (!detached)
        return null;
    const { remaining, removed } = detached;
    // 2. Don't allow dropping a wrapper INTO itself or its descendants — that
    //    would corrupt the tree. Walk the removed subtree's ids and reject if
    //    targetParentId is among them.
    if (containsId(removed, targetParentId))
        return null;
    // 3. Insert into the target.
    const next = insertAfter(remaining, targetParentId, overId, removed);
    return next;
}
function removeFromTree(blocks, id) {
    const next = [];
    let removed = null;
    for (const b of blocks) {
        if (b.id === id) {
            removed = b;
            continue;
        }
        if (b.children.length) {
            const inner = removeFromTree(b.children, id);
            if (inner) {
                removed = inner.removed;
                next.push({ ...b, children: inner.remaining });
                continue;
            }
        }
        next.push(b);
    }
    if (!removed)
        return null;
    return { remaining: next, removed };
}
function insertAfter(blocks, parentId, overId, payload) {
    // If the target parent is the root, blocks IS the parent's children list.
    // We detect "root" by trying root first, then descend.
    if (parentId === '__root__') {
        return spliceAfter(blocks, overId, payload);
    }
    let mutated = false;
    const next = blocks.map((b) => {
        if (mutated)
            return b;
        if (b.id === parentId) {
            const childInsert = spliceAfter(b.children, overId, payload);
            if (childInsert) {
                mutated = true;
                return { ...b, children: childInsert };
            }
            // Parent has no `overId` — append at end.
            mutated = true;
            return { ...b, children: [...b.children, payload] };
        }
        if (b.children.length) {
            const inner = insertAfter(b.children, parentId, overId, payload);
            if (inner) {
                mutated = true;
                return { ...b, children: inner };
            }
        }
        return b;
    });
    return mutated ? next : null;
}
function spliceAfter(arr, overId, payload) {
    const idx = arr.findIndex((b) => b.id === overId);
    if (idx < 0)
        return null;
    return [...arr.slice(0, idx + 1), payload, ...arr.slice(idx + 1)];
}
function containsId(block, id) {
    if (block.id === id)
        return true;
    for (const c of block.children) {
        if (containsId(c, id))
            return true;
    }
    return false;
}
